import { sortBy } from "lodash-es";
import type { MaybeRefOrGetter } from "vue";
import { computed, onMounted, ref, toValue, watch } from "vue";

import type { UserWithTeams } from "@/backend/http/userAdmin";
import { searchUsers as searchUsersApi, usersInTeam } from "@/backend/http/userAdmin";
import { addReactiveAlmMapping } from "@/backend/http/userAlmMapping";
import type { Team } from "@/model/session";
import type { AuthUser } from "@/model/user";
import { useUserStore } from "@/store/user";

interface UserSearchOptions {
  team: Team; // Current team to filter users by
  excludeUserIds?: MaybeRefOrGetter<string[]>; // User IDs to exclude from results (e.g., already selected user)
  includeCurrentUser?: boolean; // Whether to include current user if not on team
  addAlmMapping?: boolean; // Whether to add ALM mapping status to users
  minSearchLength?: number; // Min chars before expanding to global search (beyond team)
}

// User type with foreign flag indicating if user is outside the current team
export type UserWithForeign = AuthUser & { foreign: boolean };

/**
 * Composable for searching and filtering users with team context.
 *
 * Provides:
 * - List of users in team by default (cached)
 * - Search functionality (< minSearchLength = team only, >= minSearchLength = global search)
 * - Automatic filtering and sorting
 * - Optional ALM mapping status
 * - Reactive results
 *
 * @example
 * ```ts
 * const { users, searchQuery } = useUserSearch({
 *   team: useTeamStore().current,
 *   excludeUserIds: [selectedUser?.id],
 *   addAlmMapping: true
 * });
 * ```
 */
export function useUserSearch(options: UserSearchOptions) {
  const {
    team,
    excludeUserIds,
    includeCurrentUser = true,
    addAlmMapping = false,
    minSearchLength = 2,
  } = options;

  const searchQuery = ref("");
  const teamUsers = ref<UserWithForeign[]>([]);
  const users = ref<UserWithForeign[]>([]);
  const isSearching = ref(false);

  const currentUser = useUserStore().currentUser
    ? makeUser(useUserStore().currentUser!, false)
    : null;

  onMounted(async () => {
    const rawUsers = await usersInTeam(team);
    teamUsers.value = rawUsers.map((raw) => makeUser(raw, false));
  });

  watch(
    [searchQuery, teamUsers, () => toValue(excludeUserIds)],
    async () => {
      const isGlobalSearch = searchQuery.value.length >= minSearchLength;
      if (isGlobalSearch) isSearching.value = true;

      try {
        const searchResults = isGlobalSearch
          ? [...filteredTeamUsers(), ...(await searchNonTeamUsers())]
          : currentAndTeamUsers();

        const currentUserFirst = (user: UserWithForeign) =>
          currentUser ? user.id !== currentUser.id : false;

        const filtered = searchResults.filter(excludeById);
        users.value = sortBy(filtered, [currentUserFirst, "foreign", "name"]);
      } finally {
        isSearching.value = false;
      }
    },
    { immediate: true },
  );

  /**
   * Returns the team users, with the current user added if they aren't already in the team
   * and includeCurrentUser is true.
   */
  function currentAndTeamUsers(): UserWithForeign[] {
    if (!includeCurrentUser || !currentUser) {
      return teamUsers.value;
    }

    const userOnCurrentTeam = teamUsers.value.some((u) => u.id === currentUser.id);
    return userOnCurrentTeam ? teamUsers.value : [currentUser, ...teamUsers.value];
  }

  /**
   * Returns team users filtered by search query.
   */
  function filteredTeamUsers(): UserWithForeign[] {
    const query = searchQuery.value.toLowerCase();
    return teamUsers.value.filter((user) => user.name.toLowerCase().includes(query));
  }

  /**
   * Searches for non-team users matching the query.
   * Filters out users already in filtered team users to avoid duplicates.
   */
  async function searchNonTeamUsers(): Promise<UserWithForeign[]> {
    const filtered = filteredTeamUsers();
    return (await searchUsersApi(searchQuery.value))
      .filter((user) => !filtered.find((tu) => tu.id === user.id))
      .map((user) => makeUser(user, isForeign(user)));
  }

  /**
   * Filter predicate to exclude users by ID.
   */
  function excludeById(user: AuthUser): boolean {
    const idsToExclude = toValue(excludeUserIds) || [];
    return !idsToExclude.includes(user.id);
  }

  /**
   * Check if user is foreign to the current team.
   */
  function isForeign(user: UserWithTeams): boolean {
    return !user.teams.find((t) => t.id === team.id);
  }

  /**
   * Convert AuthUser to UserWithForeign, optionally adding ALM mapping.
   */
  function makeUser(user: AuthUser, foreign: boolean): UserWithForeign {
    const userWithForeign = { ...user, foreign };
    return addAlmMapping ? addReactiveAlmMapping(userWithForeign) : userWithForeign;
  }

  return {
    /**
     * Reactive search query. Set this to filter users.
     */
    searchQuery,

    /**
     * Reactive filtered and sorted user list.
     * - Sorted by: current user first, then non-foreign, then by name
     * - Filtered by: search query and excluded IDs
     */
    users: computed(() => users.value),

    /**
     * Whether a search for users beyond the current team is in progress.
     */
    isSearching: computed(() => isSearching.value),
  };
}
